import styled from 'styled-components';
import React, { useState, useCallback, useEffect } from 'react';
import { t, Translate } from 'react-i18nify';
import { getExt, download, TOOLBAR_HEIGHT } from '../utils';
import { file2sub, sub2vtt, sub2srt, sub2txt, vttLayoutNote } from '../libs/readSub';
import sub2ass from '../libs/readSub/sub2ass';
import googleTranslate from '../libs/googleTranslate';
import FFmpeg from '@ffmpeg/ffmpeg';
import SimpleFS from '@forlagshuset/simple-fs';

const Style = styled.div`
    /* A row of compact buttons; App.js floats it over the top right of the video area. */
    .top {
        display: flex;
        gap: 6px;
    }

    .btn {
        position: relative;
        display: flex;
        align-items: center;
        gap: 5px;
        height: ${TOOLBAR_HEIGHT}px;
        padding: 0 10px;
        border-radius: 6px;
        border: 1px solid rgb(255 255 255 / 25%);
        color: #fff;
        cursor: pointer;
        font-size: 13px;
        white-space: nowrap;
        opacity: 0.9;
        transition: opacity 0.2s ease-out;

        &:hover {
            opacity: 1;
        }

        svg {
            flex-shrink: 0;
        }
    }

    .import .btn {
        background-color: #3f51b5;
    }

    .import .file {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
    }

    .burn .btn {
        background-color: #673ab7;
    }

    .export .btn {
        background-color: #009688;
    }

    .undo .btn {
        background-color: #607d8b;
    }

    .operate {
        display: flex;
        justify-content: space-between;
        padding: 10px;
        border-bottom: 1px solid rgb(255 255 255 / 20%);

        .btn {
            position: relative;
            opacity: 0.85;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 35px;
            width: 48%;
            border-radius: 3px;
            color: #fff;
            cursor: pointer;
            font-size: 13px;
            background-color: #009688;
            transition: all 0.2s ease 0s;

            &:hover {
                opacity: 1;
            }
        }
    }

    .translate {
        display: flex;
        justify-content: space-between;
        padding: 10px;
        border-bottom: 1px solid rgb(255 255 255 / 20%);

        select {
            width: 65%;
            outline: none;
            padding: 0 5px;
            border-radius: 3px;
        }

        .btn {
            opacity: 0.85;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 35px;
            width: 33%;
            border-radius: 3px;
            color: #fff;
            cursor: pointer;
            font-size: 13px;
            background-color: #673ab7;
            transition: all 0.2s ease 0s;

            &:hover {
                opacity: 1;
            }
        }
    }

    .hotkey {
        display: flex;
        justify-content: space-between;
        padding: 10px;

        span {
            width: 49%;
            font-size: 13px;
            padding: 5px 0;
            border-radius: 3px;
            text-align: center;
            color: rgb(255 255 255 / 75%);
            background-color: rgb(255 255 255 / 20%);
        }
    }

    .bottom {
        padding: 10px;
        a {
            display: flex;
            flex-direction: column;
            border: 1px solid rgb(255 255 255 / 30%);
            text-decoration: none;

            .title {
                color: #ffeb3b;
                padding: 5px 10px;
                animation: animation 3s infinite;
                border-bottom: 1px solid rgb(255 255 255 / 30%);
            }

            @keyframes animation {
                50% {
                    color: #00bcd4;
                }
            }

            img {
                max-width: 100%;
            }
        }
    }

    .progress {
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        z-index: 9;
        height: 2px;
        background-color: rgb(0 0 0 / 50%);

        span {
            display: inline-block;
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 0;
            height: 100%;
            background-color: #ff9800;
            transition: all 0.2s ease 0s;
        }
    }
`;

FFmpeg.createFFmpeg({ log: true }).load();
const fs = new SimpleFS.FileSystem();

// Outline icons for the action buttons (same stroke style as the player's).
const Icon = ({ children }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

export default function Header({
    player,
    waveform,
    newSub,
    undoSubs,
    clearSubs,
    language,
    subtitle,
    setLoading,
    formatSub,
    setSubtitle,
    setProcessing,
    notify,
    layoutNote,
    setLayoutNote,
}) {
    const [translate, setTranslate] = useState('en');
    const [videoFile, setVideoFile] = useState(null);
    const [subtitlesLoaded, setSubtitlesLoaded] = useState(false);
    const defaultSubtitleUrl = "https://s3.eu-central-2.wasabisys.com/qira/12/2023/11/n1hr_1700922380761/n1hr_1700922380761.vtt"

    const sendSubToApi = useCallback(
        async (type) => {
            let text = '';
            const name = `${Date.now()}.${type}`;
            switch (type) {
                case 'vtt':
                    text = sub2vtt(subtitle, layoutNote);
                    break;
                default:
                    break;
            }
    
            if (!text) {
                console.error('Text is empty or invalid');
                return false;
            }
    
            const blob = new Blob([text], {type: 'text/plain'});
            const file = new File([blob], name);
    
            const params = getUrlParams(window.location.search);
            const subtitleUrl = params.subtitleUrl || defaultSubtitleUrl;
    
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('original_url', subtitleUrl);
                formData.append('auto-save', true);
    
                const response = await fetch('https://qira.mwtatooine.net/edit_subtitles', { 
                    method: 'POST',
                    body: formData
                });
    
                if (!response.ok) {
                    throw new Error('Mrežni odgovor nije bio u redu');
                }

                notify({
                    message: 'Auto-saved subtitles',
                    level: 'success',
                });
                return true;
            } catch (error) {
                console.error('Auto-save failed:', error);
                notify({
                    message: 'Auto-save failed',
                    level: 'error',
                });
                return false;
            }
        },
        [subtitle, layoutNote, defaultSubtitleUrl, notify]
    );

    // Add auto-save functionality
    useEffect(() => {
        // Only start auto-save if subtitles are loaded
        if (!subtitlesLoaded || !subtitle || subtitle.length === 0) {
            return;
        }

        const autoSaveInterval = setInterval(() => {
            sendSubToApi('vtt');
        }, 60000); // 10 seconds in milliseconds

        // Cleanup interval on component unmount
        return () => clearInterval(autoSaveInterval);
    }, [subtitle, subtitlesLoaded, sendSubToApi]);

    // When embedded, the host can ask for an immediate save (e.g. before it
    // reloads us with new layout settings) and is told whether it worked.
    useEffect(() => {
        if (!window.parent || window.parent === window) {
            return;
        }

        const onMessage = async (event) => {
            if (event.source !== window.parent || !event.data || event.data.type !== 'subplayer:save') {
                return;
            }
            // Before the file has loaded there is nothing to save; never send an empty VTT.
            const saved = subtitlesLoaded && subtitle && subtitle.length > 0 && (await sendSubToApi('vtt'));
            event.source.postMessage({ type: saved ? 'subplayer:saved' : 'subplayer:save-failed' }, '*');
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [subtitle, subtitlesLoaded, sendSubToApi]);

    const decodeAudioData = useCallback(
        async (file) => {
            try {
                const { createFFmpeg, fetchFile } = FFmpeg;
                const ffmpeg = createFFmpeg({ log: true });
                ffmpeg.setProgress(({ ratio }) => setProcessing(ratio * 100));
                setLoading(t('LOADING_FFMPEG'));
                await ffmpeg.load();
                ffmpeg.FS('writeFile', file.name, await fetchFile(file));
                setLoading('');
                notify({
                    message: t('DECODE_START'),
                    level: 'info',
                });
                const output = `${Date.now()}.mp3`;
                await ffmpeg.run('-i', file.name, '-ac', '1', '-ar', '8000', output);
                const uint8 = ffmpeg.FS('readFile', output);
                // download(URL.createObjectURL(new Blob([uint8])), `${output}`);
                await waveform.decoder.decodeAudioData(uint8);
                waveform.drawer.update();
                setProcessing(0);
                ffmpeg.setProgress(() => null);
                notify({
                    message: t('DECODE_SUCCESS'),
                    level: 'success',
                });
            } catch (error) {
                setLoading('');
                setProcessing(0);
                notify({
                    message: t('DECODE_ERROR'),
                    level: 'error',
                });
            }
        },
        [waveform, notify, setProcessing, setLoading],
    );

    function getUrlParams(search) {
        const hashes = search.slice(search.indexOf('?') + 1).split('&');
        return hashes.reduce((params, hash) => {
          const split = hash.indexOf('=');
      
          if (split < 0) {
            return Object.assign(params, {[hash]: null});
          }
      
          const key = hash.slice(0, split);
          const val = hash.slice(split + 1);
          return Object.assign(params, {[key]: decodeURIComponent(val)});
        }, {});
      }
      

      const loadSubtitlesFromUrl = useCallback(async () => {
        if (subtitlesLoaded) {
          return;
        }
      
        const params = getUrlParams(window.location.search);
        const subtitleUrl = params.subtitleUrl || defaultSubtitleUrl;
      
        try {
          const response = await fetch(subtitleUrl);
          const text = await response.text();
      
          file2sub(new File([text], "subtitles.vtt"))
            .then((processedSubtitles) => {
              clearSubs();
              setLayoutNote(vttLayoutNote(text));
              setSubtitle(processedSubtitles);
              setSubtitlesLoaded(true); // Označite da su titlovi učitani
            })
            .catch((error) => {
              notify({
                message: error.message,
                level: 'error',
              });
            });
        } catch (error) {
          notify({
            message: error.message,
            level: 'error',
          });
        }
      }, [setSubtitle, setLayoutNote, clearSubs, notify, subtitlesLoaded, defaultSubtitleUrl]);
      
    
    useEffect(() => {
        loadSubtitlesFromUrl();
    }, []);
    
    // Funkcija za dobijanje vrednosti kolačića
    function getCookie(name) {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [cookieName, cookieValue] = cookie.split('=');
            if (cookieName.trim() === name) {
                return cookieValue;
            }
        }
        return null;
    }
    

    const burnSubtitles = useCallback(async () => {
        try {
            const { createFFmpeg, fetchFile } = FFmpeg;
            const ffmpeg = createFFmpeg({ log: true });
            ffmpeg.setProgress(({ ratio }) => setProcessing(ratio * 100));
            setLoading(t('LOADING_FFMPEG'));
            await ffmpeg.load();
            setLoading(t('LOADING_FONT'));

            await fs.mkdir('/fonts');
            const fontExist = await fs.exists('/fonts/Microsoft-YaHei.ttf');
            if (fontExist) {
                const fontBlob = await fs.readFile('/fonts/Microsoft-YaHei.ttf');
                ffmpeg.FS('writeFile', `tmp/Microsoft-YaHei.ttf`, await fetchFile(fontBlob));
            } else {
                const fontUrl = 'https://cdn.jsdelivr.net/gh/zhw2590582/SubPlayer/docs/Microsoft-YaHei.ttf';
                const fontBlob = await fetch(fontUrl).then((res) => res.blob());
                await fs.writeFile('/fonts/Microsoft-YaHei.ttf', fontBlob);
                ffmpeg.FS('writeFile', `tmp/Microsoft-YaHei.ttf`, await fetchFile(fontBlob));
            }
            setLoading(t('LOADING_VIDEO'));
            ffmpeg.FS(
                'writeFile',
                videoFile ? videoFile.name : 'sample.mp4',
                await fetchFile(videoFile || 'sample.mp4'),
            );
            setLoading(t('LOADING_SUB'));
            const subtitleFile = new File([new Blob([sub2ass(subtitle)])], 'subtitle.ass');
            ffmpeg.FS('writeFile', subtitleFile.name, await fetchFile(subtitleFile));
            setLoading('');
            notify({
                message: t('BURN_START'),
                level: 'info',
            });
            const output = `${Date.now()}.mp4`;
            await ffmpeg.run(
                '-i',
                videoFile ? videoFile.name : 'sample.mp4',
                '-vf',
                `ass=${subtitleFile.name}:fontsdir=/tmp`,
                '-preset',
                videoFile ? 'fast' : 'ultrafast',
                output,
            );
            const uint8 = ffmpeg.FS('readFile', output);
            download(URL.createObjectURL(new Blob([uint8])), `${output}`);
            setProcessing(0);
            ffmpeg.setProgress(() => null);
            notify({
                message: t('BURN_SUCCESS'),
                level: 'success',
            });
        } catch (error) {
            setLoading('');
            setProcessing(0);
            notify({
                message: t('BURN_ERROR'),
                level: 'error',
            });
        }
    }, [notify, setProcessing, setLoading, videoFile, subtitle]);

    const onVideoChange = useCallback(
        (event) => {
            const file = event.target.files[0];
            if (file) {
                const ext = getExt(file.name);
                const canPlayType = player.canPlayType(file.type);
                if (canPlayType === 'maybe' || canPlayType === 'probably') {
                    setVideoFile(file);
                    decodeAudioData(file);
                    const url = URL.createObjectURL(new Blob([file]));
                    waveform.decoder.destroy();
                    waveform.drawer.update();
                    waveform.seek(0);
                    player.currentTime = 0;
                    clearSubs();
                    setSubtitle([
                        newSub({
                            start: '00:00:00.000',
                            end: '00:00:01.000',
                            text: t('SUB_TEXT'),
                        }),
                    ]);
                    player.src = url;
                } else {
                    notify({
                        message: `${t('VIDEO_EXT_ERR')}: ${file.type || ext}`,
                        level: 'error',
                    });
                }
            }
        },
        [newSub, notify, player, setSubtitle, waveform, clearSubs, decodeAudioData],
    );

    const onSubtitleChange = useCallback(
        (event) => {
            const file = event.target.files[0];
            if (file) {
                const ext = getExt(file.name);
                if (['ass', 'vtt', 'srt', 'json'].includes(ext)) {
                    file2sub(file)
                        .then((res) => {
                            clearSubs();
                            setSubtitle(res);
                        })
                        .catch((err) => {
                            notify({
                                message: err.message,
                                level: 'error',
                            });
                        });
                } else {
                    notify({
                        message: `${t('SUB_EXT_ERR')}: ${ext}`,
                        level: 'error',
                    });
                }
            }
        },
        [notify, setSubtitle, clearSubs],
    );

    const onInputClick = useCallback((event) => {
        event.target.value = '';
    }, []);

    const downloadSub = useCallback(
        (type) => {
            let text = '';
            const name = `${Date.now()}.${type}`;
            switch (type) {
                case 'vtt':
                    text = sub2vtt(subtitle, layoutNote);
                    break;
                case 'srt':
                    text = sub2srt(subtitle);
                    break;
                case 'ass':
                    text = sub2ass(subtitle);
                    break;
                case 'txt':
                    text = sub2txt(subtitle);
                    break;
                case 'json':
                    text = JSON.stringify(subtitle);
                    break;
                default:
                    break;
            }

            if (!text) {
                console.error('Text is empty or invalid');
                return;
            } else{
                console.error('Prosao');
            }
    

            const url = URL.createObjectURL(new Blob([text]));
            download(url, name);
        },
        [subtitle, layoutNote],
    );

    const onTranslate = useCallback(() => {
        setLoading(t('TRANSLATING'));
        googleTranslate(formatSub(subtitle), translate)
            .then((res) => {
                setLoading('');
                setSubtitle(formatSub(res));
                notify({
                    message: t('TRANSLAT_SUCCESS'),
                    level: 'success',
                });
            })
            .catch((err) => {
                setLoading('');
                notify({
                    message: err.message,
                    level: 'error',
                });
            });
    }, [subtitle, setLoading, formatSub, setSubtitle, translate, notify]);

    return (
        <Style className="tool">
            <div className="top">
                {/* <div className="import">
                    <div className="btn">
                        <Translate value="OPEN_VIDEO" />
                        <input className="file" type="file" onChange={onVideoChange} onClick={onInputClick} />
                    </div>
                    <div className="btn">
                        <Translate value="OPEN_SUB" />
                        <input className="file" type="file" onChange={onSubtitleChange} onClick={onInputClick} />
                    </div>
                </div> */}
                {window.crossOriginIsolated ? (
                    <div className="burn" onClick={burnSubtitles}>
                        <div className="btn">
                            <Icon>
                                <rect x="2" y="5" width="14" height="14" rx="2" />
                                <path d="M16 10l6-3v10l-6-3z" />
                            </Icon>
                            <Translate value="EXPORT_VIDEO" />
                        </div>
                    </div>
                ) : null}
                <div className="export">
                    {/* <div className="btn" onClick={() => downloadSub('ass')}>
                        <Translate value="EXPORT_ASS" />
                    </div>
                    <div className="btn" onClick={() => downloadSub('srt')}>
                        <Translate value="EXPORT_SRT" />
                    </div> */}
                    <div className="btn" onClick={() => downloadSub('vtt')}>
                        <Icon>
                            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                            <path d="M7 11l5 5l5-5" />
                            <path d="M12 4v12" />
                        </Icon>
                        <Translate value="EXPORT_VTT" />
                    </div>
                </div>
                <div className="import"  onClick={() => sendSubToApi('vtt')}>
                    <div className="btn">
                        <Icon>
                            <path d="M6 4h10l4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2" />
                            <circle cx="12" cy="14" r="2" />
                            <path d="M14 4v4H8V4" />
                        </Icon>
                        <Translate value="Save file" />
                    </div>
                </div>
                <div className="undo" onClick={undoSubs} title="Ctrl + Z / ⌘ + Z">
                    <div className="btn">
                        <Icon>
                            <path d="M9 14l-4-4l4-4" />
                            <path d="M5 10h11a4 4 0 1 1 0 8h-1" />
                        </Icon>
                        <Translate value="UNDO" />
                    </div>
                </div>
                {/* <div className="">
                    <div
                        className="btn"
                        onClick={() => {
                            if (window.confirm(t('CLEAR_TIP')) === true) {
                                clearSubs();
                                window.location.reload();
                            }
                        }}
                    >
                        <Translate value="CLEAR" />
                    </div>
                    <div className="btn" onClick={undoSubs}>
                        <Translate value="UNDO" />
                    </div>
                </div>
                <div className="translate">
                    <select value={translate} onChange={(event) => setTranslate(event.target.value)}>
                        {(languages[language] || languages.en).map((item) => (
                            <option key={item.key} value={item.key}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    <div className="btn" onClick={onTranslate}>
                        <Translate value="TRANSLATE" />
                    </div>
                </div>
                <div className="hotkey">
                    <span>
                        <Translate value="HOTKEY_01" />
                    </span>
                    <span>
                        <Translate value="HOTKEY_02" />
                    </span>
                </div> */}
            </div>
            {/* <div className="bottom">
                <a href="https://online.aimu-app.com/">
                    <div className="title">全新字幕编辑器来了，点击这里体验</div>
                    <img src="/aimu.png" alt="aimu" />
                </a>
            </div> */}
        </Style>
    );
}
